import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AppointmentItem,
  AppointmentWarning,
  ClientDetail,
  ClientListItem,
  ServiceItem,
  UserDirectoryItem,
} from '@crm/shared';
import { useAuth } from '../auth/AuthContext';
import { useClientMainNote } from '../clients/mainNoteApi';
import { useLoyaltyCard } from '../loyalty/api';
import { useServicesList } from '../services/api';
import {
  extractCompletionError,
  extractWarnings,
  useAppointmentCancel,
  useAppointmentComplete,
  useAppointmentCreate,
  useAppointmentNoShow,
  useAppointmentUncomplete,
  useAppointmentUpdate,
} from './api';
import { ClientPicker } from './ClientPicker';
import { NotesSection } from './NotesSection';
import { extractApiError } from '../../lib/api';

export type ModalInitial =
  | { mode: 'create'; startAt: Date | null; employeeId: number | null }
  | { mode: 'edit'; appointment: AppointmentItem };

type Props = {
  initial: ModalInitial;
  onClose: () => void;
  employees: UserDirectoryItem[];
};

type FormState = {
  client: ClientDetail | ClientListItem | null;
  employeeId: number | null;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  serviceIds: number[];
  status: 'BOOKED' | 'CONFIRMED';
};

function dateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function timeOnly(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function buildIsoLocal(date: string, time: string): string {
  // Interpretujemy w lokalnej strefie procesu (przeglądarka). Backend
  // zapisuje w UTC, ale dostaje konkretny instant.
  return new Date(`${date}T${time}:00`).toISOString();
}

function initialFormFromAppointment(a: AppointmentItem): FormState {
  const start = new Date(a.startAt);
  return {
    client: {
      id: a.client.id,
      firstName: a.client.firstName,
      lastName: a.client.lastName,
      phone: a.client.phone,
      email: null,
      marketingConsent: false,
      rodoConsent: false,
      anonymizedAt: a.client.anonymizedAt,
      appointmentsCount: 0,
      // Faza 18: pole `mainNote` w ClientListItem jest wymagane przez typ —
      // ale realny podgląd notatki w modalu wyciągamy dedykowanym hookiem
      // useClientMainNote (zawsze aktualny). Placeholder NULL.
      mainNote: null,
      createdAt: '',
      updatedAt: '',
    },
    employeeId: a.employeeId,
    date: dateOnly(start),
    time: timeOnly(start),
    serviceIds: a.services.map((s) => s.serviceId),
    status: a.status === 'CONFIRMED' ? 'CONFIRMED' : 'BOOKED',
  };
}

function initialFormFromCreate(initial: ModalInitial & { mode: 'create' }): FormState {
  const startAt = initial.startAt ?? new Date();
  return {
    client: null,
    employeeId: initial.employeeId,
    date: dateOnly(startAt),
    time: timeOnly(startAt),
    serviceIds: [],
    status: 'BOOKED',
  };
}

export function AppointmentModal({ initial, onClose, employees }: Props): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEdit = initial.mode === 'edit';
  const apt = initial.mode === 'edit' ? initial.appointment : null;

  const isTerminal =
    apt !== null && (apt.status === 'CANCELLED' || apt.status === 'COMPLETED' || apt.status === 'NO_SHOW');

  const [form, setForm] = useState<FormState>(() =>
    initial.mode === 'edit'
      ? initialFormFromAppointment(initial.appointment)
      : initialFormFromCreate(initial),
  );
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<AppointmentWarning[] | null>(null);

  const servicesQuery = useServicesList({ active: 'true' });
  const services = servicesQuery.data ?? [];

  // Sumaryczna długość i cena z wybranych usług (snapshot z katalogu —
  // backend bierze własny snapshot przy zapisie; tu tylko podgląd).
  const totals = useMemo(() => {
    const selected = services.filter((s) => form.serviceIds.includes(s.id));
    const duration = selected.reduce((acc, s) => acc + s.durationMinutes, 0);
    const price = selected.reduce((acc, s) => acc + Number(s.price), 0);
    return { duration, price };
  }, [services, form.serviceIds]);

  // Karta lojalnościowa wybranego klienta (Faza 15). EMPLOYEE nie ma dostępu
  // do endpointa → wyłączamy zapytanie (banner nie pokaże się przy widoku
  // pracownika; pracownik widzi tier dopiero w `discount` na istniejącej
  // wizycie po zapisie przez recepcję).
  const canViewLoyalty = user?.role === 'ADMIN' || user?.role === 'RECEPTION';
  const loyaltyClientId =
    form.client && !form.client.anonymizedAt ? form.client.id : null;
  const loyaltyQuery = useLoyaltyCard(loyaltyClientId, canViewLoyalty);
  const loyaltyCard = loyaltyQuery.data ?? null;
  const previewDiscount = useMemo(() => {
    if (!loyaltyCard || loyaltyCard.effectivePercent <= 0) return null;
    const pct = loyaltyCard.effectivePercent;
    const discounted = totals.price * (1 - pct / 100);
    const saved = totals.price - discounted;
    return {
      percent: pct,
      tier: loyaltyCard.automaticTier,
      manual: loyaltyCard.manualDiscountPercent,
      originalPrice: totals.price,
      discountedPrice: discounted,
      saved,
    };
  }, [loyaltyCard, totals.price]);

  // Faza 18: główna notatka klienta — odświeżana automatycznie po zmianie
  // klienta. Pobranie respektuje visibility (backend zwraca null gdy viewer
  // nie widzi notatki prywatnej).
  const mainNoteQuery = useClientMainNote(form.client?.id ?? null);
  const clientMainNote = mainNoteQuery.data ?? null;

  const create = useAppointmentCreate();
  const update = useAppointmentUpdate(apt?.id ?? 0);
  const cancel = useAppointmentCancel(apt?.id ?? 0);
  const complete = useAppointmentComplete(apt?.id ?? 0);
  const uncomplete = useAppointmentUncomplete(apt?.id ?? 0);
  const noShow = useAppointmentNoShow(apt?.id ?? 0);

  const canEdit = !isTerminal;
  const canMarkOwnTerminal =
    apt !== null && user !== null &&
    (user.role === 'ADMIN' || user.role === 'RECEPTION' || apt.employeeId === user.id);

  // Faza 19 — kontekst zatwierdzania zaległej wizyty.
  // OVERDUE_THRESHOLD_DAYS jest dublowany z env backendu — tylko hint UI.
  // Końcową decyzję podejmuje backend (zwróci 409 OVERDUE_COMPLETION).
  const OVERDUE_THRESHOLD_DAYS = 30;
  const UNCOMPLETE_WINDOW_HOURS = 24;
  const isAdmin = user?.role === 'ADMIN';
  const ageInDays = apt
    ? Math.floor((Date.now() - new Date(apt.startAt).getTime()) / 86_400_000)
    : 0;
  const isOverdue = apt !== null && ageInDays > OVERDUE_THRESHOLD_DAYS;
  const completedAtMs = apt?.completedAt ? new Date(apt.completedAt).getTime() : null;
  const completedHoursAgo =
    completedAtMs !== null ? (Date.now() - completedAtMs) / 3_600_000 : null;
  const isAuthorOfCompletion =
    apt !== null && user !== null && apt.completedById === user.id;
  const canUncomplete =
    apt !== null &&
    apt.status === 'COMPLETED' &&
    user !== null &&
    (isAdmin ||
      (isAuthorOfCompletion &&
        completedHoursAgo !== null &&
        completedHoursAgo <= UNCOMPLETE_WINDOW_HOURS));

  function patchForm<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleService(id: number): void {
    setForm((f) => ({
      ...f,
      serviceIds: f.serviceIds.includes(id)
        ? f.serviceIds.filter((x) => x !== id)
        : [...f.serviceIds, id],
    }));
  }

  function validate(): string | null {
    if (!form.client) return t('appointments.errors.clientRequired');
    if (!form.employeeId) return t('appointments.errors.employeeRequired');
    if (form.serviceIds.length === 0) return t('appointments.errors.servicesRequired');
    if (!form.date || !form.time) return t('appointments.errors.timeRequired');
    return null;
  }

  async function submit(force = false): Promise<void> {
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    const startAt = buildIsoLocal(form.date, form.time);

    try {
      if (isEdit && apt) {
        await update.mutateAsync({
          startAt,
          employeeId: form.employeeId ?? undefined,
          services: form.serviceIds.map((id) => ({ serviceId: id })),
          status: form.status,
          force,
        });
      } else {
        await create.mutateAsync({
          clientId: form.client!.id,
          employeeId: form.employeeId!,
          startAt,
          services: form.serviceIds.map((id) => ({ serviceId: id })),
          status: form.status,
          force,
        });
      }
      setWarnings(null);
      onClose();
    } catch (err) {
      const w = extractWarnings(err);
      if (w.length > 0) {
        setWarnings(w);
      } else {
        setError(extractApiError(err));
      }
    }
  }

  async function handleCancelAppointment(): Promise<void> {
    if (!apt) return;
    const reason = window.prompt(t('appointments.cancel.askReason') ?? '');
    if (reason === null) return; // anulował dialog
    try {
      await cancel.mutateAsync({ reason: reason || null });
      onClose();
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  async function handleComplete(): Promise<void> {
    if (!apt) return;
    // Faza 19 — dla wizyt >30 dni pokazujemy ostrzeżenie z liczbą dni.
    // Klucz `complete.confirmOverdue` używa `{days}` — i18next interpoluje.
    const confirmKey = isOverdue
      ? 'appointments.complete.confirmOverdue'
      : 'appointments.complete.confirm';
    const msg = isOverdue
      ? t(confirmKey, { days: ageInDays })
      : t(confirmKey);
    if (!window.confirm(msg ?? '')) return;
    try {
      // ADMIN przy wizycie zaległej wysyła `force=true`; w pozostałych
      // przypadkach pusty payload (działa tak samo jak dotąd).
      await complete.mutateAsync(isOverdue && isAdmin ? { force: true } : undefined);
      onClose();
    } catch (err) {
      const completionErr = extractCompletionError(err);
      if (completionErr?.code === 'OVERDUE_COMPLETION') {
        setError(
          t('appointments.complete.overdueBlocked', {
            days: completionErr.ageInDays,
          }) ?? '',
        );
        return;
      }
      setError(extractApiError(err));
    }
  }

  async function handleUncomplete(): Promise<void> {
    if (!apt) return;
    // Confirm z kwotą zwrotu z karty lojalnościowej.
    const msg = t('appointments.uncomplete.confirm', {
      amount: apt.totalPrice,
    });
    if (!window.confirm(msg ?? '')) return;
    try {
      await uncomplete.mutateAsync();
      onClose();
    } catch (err) {
      const completionErr = extractCompletionError(err);
      if (completionErr?.code === 'UNCOMPLETE_OUT_OF_WINDOW') {
        setError(
          t('appointments.uncomplete.outOfWindow', {
            hours: completionErr.windowHours,
          }) ?? '',
        );
        return;
      }
      setError(extractApiError(err));
    }
  }

  async function handleNoShow(): Promise<void> {
    if (!apt) return;
    if (!window.confirm(t('appointments.noShow.confirm') ?? '')) return;
    try {
      await noShow.mutateAsync();
      onClose();
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  // Grupowanie usług po kategorii — UI z listy specjalizacji.
  const servicesByCategory = useMemo(() => {
    const map = new Map<string, ServiceItem[]>();
    for (const s of services) {
      const cat = s.category ?? '—';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [services]);

  const employeeOptions = employees.filter((e) => e.bookable && e.active);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-200 p-4">
          <h2 className="text-lg font-semibold text-zinc-900">
            {isEdit ? t('appointments.modal.titleEdit') : t('appointments.modal.titleCreate')}
            {apt && (
              <span className="ml-2 text-xs uppercase text-zinc-500">
                · {t(`appointments.status.${apt.status}`)}
              </span>
            )}
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
            ✕
          </button>
        </div>

        {clientMainNote && form.client && (
          <MainNoteBanner note={clientMainNote} clientId={form.client.id} />
        )}

        <div className="space-y-4 p-4">
          {/* Klient */}
          <section>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('appointments.field.client')}
            </label>
            <ClientPicker
              value={form.client}
              onChange={(c) => patchForm('client', c)}
              disabled={!canEdit}
            />
          </section>

          {/* Pracownik */}
          <section>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('appointments.field.employee')}
            </label>
            <select
              className="input"
              value={form.employeeId ?? ''}
              onChange={(e) =>
                patchForm('employeeId', e.target.value ? Number(e.target.value) : null)
              }
              disabled={!canEdit || (user?.role === 'EMPLOYEE')}
            >
              <option value="">— {t('appointments.field.choose')} —</option>
              {employeeOptions.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </section>

          {/* Data + godzina */}
          <section className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                {t('appointments.field.date')}
              </label>
              <input
                type="date"
                className="input"
                value={form.date}
                onChange={(e) => patchForm('date', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                {t('appointments.field.time')}
              </label>
              <input
                type="time"
                step="900"
                className="input"
                value={form.time}
                onChange={(e) => patchForm('time', e.target.value)}
                disabled={!canEdit}
              />
            </div>
          </section>

          {/* Status (edycja) */}
          {isEdit && canEdit && (
            <section>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                {t('appointments.field.status')}
              </label>
              <select
                className="input"
                value={form.status}
                onChange={(e) => patchForm('status', e.target.value as 'BOOKED' | 'CONFIRMED')}
              >
                <option value="BOOKED">{t('appointments.status.BOOKED')}</option>
                <option value="CONFIRMED">{t('appointments.status.CONFIRMED')}</option>
              </select>
            </section>
          )}

          {/* Banner lojalnościowy (Faza 15): pokazujemy zniżkę zaplanowaną
              dla wybranego klienta. Tylko przy CREATE — w EDIT pokazujemy
              snapshot z `apt.discount` niżej. */}
          {!isEdit && previewDiscount && (
            <section className="rounded-lg border border-brand-200 bg-brand-50/70 px-3 py-2 text-sm text-brand-900">
              <div className="font-semibold">
                {previewDiscount.manual > previewDiscount.percent - 1 &&
                previewDiscount.tier === 0
                  ? t('appointments.loyalty.bannerManual', {
                      percent: previewDiscount.percent,
                    })
                  : previewDiscount.tier === 2
                    ? t('appointments.loyalty.bannerVip', {
                        percent: previewDiscount.percent,
                      })
                    : t('appointments.loyalty.bannerRegular', {
                        percent: previewDiscount.percent,
                      })}
              </div>
              {totals.price > 0 && (
                <div className="mt-0.5 text-xs">
                  {t('appointments.loyalty.savings', {
                    original: previewDiscount.originalPrice.toFixed(2),
                    discounted: previewDiscount.discountedPrice.toFixed(2),
                    saved: previewDiscount.saved.toFixed(2),
                  })}
                </div>
              )}
            </section>
          )}

          {/* Snapshot zniżki dla istniejącej wizyty (Faza 15). */}
          {isEdit && apt && apt.discount.percent > 0 && (
            <section className="rounded-lg border border-brand-200 bg-brand-50/70 px-3 py-2 text-sm text-brand-900">
              <div className="font-semibold">
                {t('appointments.loyalty.applied', {
                  percent: apt.discount.percent,
                  amount: apt.discount.amount,
                })}
              </div>
              <div className="mt-0.5 text-xs">
                {t('appointments.loyalty.appliedHint', {
                  original: apt.discount.originalTotalPrice,
                  paid: apt.totalPrice,
                })}
              </div>
            </section>
          )}

          {/* Usługi */}
          <section>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-sm font-medium text-zinc-700">
                {t('appointments.field.services')}
              </label>
              <span className="text-xs text-zinc-500">
                {t('appointments.summary', {
                  duration: totals.duration,
                  price: totals.price.toFixed(2),
                })}
              </span>
            </div>
            <div className="max-h-48 space-y-2 overflow-auto rounded-lg border border-zinc-200 p-2">
              {servicesByCategory.length === 0 && (
                <div className="text-sm text-zinc-500">{t('appointments.field.noServices')}</div>
              )}
              {servicesByCategory.map(([cat, items]) => (
                <div key={cat}>
                  <div className="mb-1 text-xs font-semibold uppercase text-zinc-500">{cat}</div>
                  {items.map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 rounded px-2 py-1 hover:bg-zinc-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.serviceIds.includes(s.id)}
                        onChange={() => toggleService(s.id)}
                        disabled={!canEdit}
                      />
                      <span className="flex-1 text-sm">{s.name}</span>
                      <span className="text-xs text-zinc-500">
                        {s.durationMinutes}m · {Number(s.price).toFixed(2)} zł
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* Warnings */}
          {warnings && warnings.length > 0 && (
            <section className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <div className="mb-2 text-sm font-semibold text-amber-900">
                {t('appointments.warnings.title')}
              </div>
              <ul className="mb-3 list-disc pl-5 text-sm text-amber-900">
                {warnings.map((w, i) => (
                  <li key={i}>{w.message}</li>
                ))}
              </ul>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void submit(true)}
                  className="btn-primary"
                >
                  {t('appointments.warnings.forceSave')}
                </button>
                <button
                  type="button"
                  onClick={() => setWarnings(null)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </section>
          )}

          {error && <div className="rounded bg-red-50 p-2 text-sm text-red-700">{error}</div>}

          {/* Notatki — dostępne tylko dla istniejącej wizyty (po zapisie). */}
          {isEdit && apt && <NotesSection appointmentId={apt.id} />}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-200 p-4">
          <div className="flex gap-2">
            {isEdit && !isTerminal && canMarkOwnTerminal && (
              <>
                {/* Faza 19 — dla wizyt >30 dni nie-ADMIN dostaje wyłączony przycisk
                    z tooltipem; ADMIN dostaje warning styling + confirm w handleComplete. */}
                <button
                  type="button"
                  onClick={() => void handleComplete()}
                  disabled={isOverdue && !isAdmin}
                  title={
                    isOverdue && !isAdmin
                      ? (t('appointments.complete.adminOnlyHint', { days: ageInDays }) ?? '')
                      : undefined
                  }
                  className={
                    isOverdue
                      ? 'rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50'
                      : 'rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100'
                  }
                >
                  {isOverdue && isAdmin
                    ? t('appointments.actions.forceComplete')
                    : t('appointments.actions.complete')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleNoShow()}
                  className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                >
                  {t('appointments.actions.noShow')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleCancelAppointment()}
                  className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100"
                >
                  {t('appointments.actions.cancel')}
                </button>
              </>
            )}
            {/* Faza 19 — cofnięcie statusu COMPLETED. Widoczne dla autora
                w oknie 24h LUB dla ADMINA bez limitu. Pełny rollback karty
                lojalnościowej leci po stronie backendu. */}
            {isEdit && apt?.status === 'COMPLETED' && canUncomplete && (
              <button
                type="button"
                onClick={() => void handleUncomplete()}
                className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-800 hover:bg-orange-100"
              >
                {t('appointments.actions.uncomplete')}
              </button>
            )}
          </div>

          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => void submit(false)}
                disabled={create.isPending || update.isPending}
                className="btn-primary"
              >
                {create.isPending || update.isPending
                  ? t('common.saving')
                  : t('common.save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sticky banner z główną notatką klienta w modalu wizyty (Faza 18).
 * Read-only — edycja w karcie klienta (otwiera w nowej karcie, żeby nie
 * zgubić rozpoczętej wizyty). Pełna treść z `whitespace-pre-wrap` —
 * mainNote.body, nie preview (banner i tak ma `max-h-32 overflow-auto`).
 */
function MainNoteBanner({
  note,
  clientId,
}: {
  note: import('@crm/shared').ClientMainNote;
  clientId: number;
}): ReactNode {
  const { t } = useTranslation();
  const dateFmt = new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const editorName = `${note.lastEditedBy.firstName} ${note.lastEditedBy.lastName}`;
  return (
    <div className="sticky top-0 z-10 border-b border-amber-300 bg-amber-50 px-4 py-3">
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-0.5 text-base">
          📌
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
            {t('clientNote.bannerTitle')}
            {note.visibility === 'EMPLOYEE' && (
              <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-amber-900">
                🔒 {t('clientNote.visibility.EMPLOYEE')}
              </span>
            )}
          </div>
          <p className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap text-sm text-amber-950">
            {note.body}
          </p>
          <div className="mt-1 text-[11px] text-amber-800/80">
            {t('clientNote.lastEditedBy', {
              name: editorName,
              date: dateFmt.format(new Date(note.lastEditedAt)),
            })}
          </div>
        </div>
        <a
          href={`/clients/${clientId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-xs font-medium text-amber-800 underline-offset-2 hover:underline"
        >
          {t('clientNote.editInProfile')}
        </a>
      </div>
    </div>
  );
}
